// src/services/api/cache.ts
import { apiClient, buildQueryParams } from './base';
import { CacheInfo, CacheStats } from '../../types/api';

export class CacheService {
  private readonly baseUrl = '/cache';

  async getCacheStats(): Promise<CacheStats> {
    const response = await apiClient.get<CacheStats>(`${this.baseUrl}/stats`);
    return response.data;
  }

  async getCacheKeys(prefix?: string): Promise<string[]> {
    const params = prefix ? { prefix } : {};
    const queryString = buildQueryParams(params);
    const url = queryString ? `${this.baseUrl}/keys?${queryString}` : `${this.baseUrl}/keys`;
    const response = await apiClient.get<string[]>(url);
    return response.data;
  }

  async getCacheValue(key: string): Promise<CacheInfo> {
    const response = await apiClient.get<CacheInfo>(`${this.baseUrl}/value/${encodeURIComponent(key)}`);
    return response.data;
  }

  async setCacheValue(key: string, value: any, ttl?: number): Promise<void> {
    const response = await apiClient.post(`${this.baseUrl}/value`, { key, value, ttl });
    if (!response.success) {
      throw new Error(response.message || 'Errore durante l\'impostazione del valore cache');
    }
  }

  async deleteCacheKey(key: string): Promise<void> {
    const response = await apiClient.delete(`${this.baseUrl}/key/${encodeURIComponent(key)}`);
    if (!response.success) {
      throw new Error(response.message || 'Errore durante l\'eliminazione della chiave cache');
    }
  }

  async deleteCacheKeys(keys: string[]): Promise<void> {
    const response = await apiClient.delete(`${this.baseUrl}/keys`, { data: { keys } });
    if (!response.success) {
      throw new Error(response.message || 'Errore durante l\'eliminazione delle chiavi cache');
    }
  }

  async clearCacheByPrefix(prefix: string): Promise<{ deleted: number }> {
    const response = await apiClient.delete<{ deleted: number }>(`${this.baseUrl}/prefix/${encodeURIComponent(prefix)}`);
    return response.data;
  }

  async clearAllCache(): Promise<{ deleted: number }> {
    const response = await apiClient.delete<{ deleted: number }>(`${this.baseUrl}/all`);
    return response.data;
  }

  async warmUpCache(keys: string[]): Promise<{ warmed: number; errors: string[] }> {
    const response = await apiClient.post<{ warmed: number; errors: string[] }>(`${this.baseUrl}/warmup`, { keys });
    return response.data;
  }

  async refreshCache(key: string): Promise<void> {
    const response = await apiClient.post(`${this.baseUrl}/refresh`, { key });
    if (!response.success) {
      throw new Error(response.message || 'Errore durante il refresh della cache');
    }
  }

  async getCacheHealth(): Promise<{
    status: 'healthy' | 'warning' | 'error';
    memoryUsage: number;
    hitRate: number;
    missRate: number;
    evictionRate: number;
    connectionCount: number;
    lastCheck: string;
  }> {
    const response = await apiClient.get<{
      status: 'healthy' | 'warning' | 'error';
      memoryUsage: number;
      hitRate: number;
      missRate: number;
      evictionRate: number;
      connectionCount: number;
      lastCheck: string;
    }>(`${this.baseUrl}/health`);
    return response.data;
  }

  async getCacheMetrics(timeRange: '1h' | '24h' | '7d' | '30d' = '24h'): Promise<{
    timestamp: string;
    hits: number;
    misses: number;
    evictions: number;
    memoryUsage: number;
  }[]> {
    const response = await apiClient.get<{
      timestamp: string;
      hits: number;
      misses: number;
      evictions: number;
      memoryUsage: number;
    }[]>(`${this.baseUrl}/metrics?timeRange=${timeRange}`);
    return response.data;
  }

  async getTopKeys(limit: number = 10): Promise<{
    key: string;
    hits: number;
    misses: number;
    lastAccessed: string;
    size: number;
  }[]> {
    const response = await apiClient.get<{
      key: string;
      hits: number;
      misses: number;
      lastAccessed: string;
      size: number;
    }[]>(`${this.baseUrl}/top-keys?limit=${limit}`);
    return response.data;
  }

  async getExpiredKeys(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`${this.baseUrl}/expired`);
    return response.data;
  }

  async cleanupExpiredKeys(): Promise<{ deleted: number }> {
    const response = await apiClient.post<{ deleted: number }>(`${this.baseUrl}/cleanup-expired`);
    return response.data;
  }

  async setCachePolicy(key: string, policy: {
    ttl?: number;
    maxSize?: number;
    evictionPolicy?: 'lru' | 'lfu' | 'fifo';
  }): Promise<void> {
    const response = await apiClient.post(`${this.baseUrl}/policy`, { key, policy });
    if (!response.success) {
      throw new Error(response.message || 'Errore durante l\'impostazione della policy cache');
    }
  }

  async getCachePolicy(key: string): Promise<{
    ttl?: number;
    maxSize?: number;
    evictionPolicy?: 'lru' | 'lfu' | 'fifo';
  }> {
    const response = await apiClient.get<{
      ttl?: number;
      maxSize?: number;
      evictionPolicy?: 'lru' | 'lfu' | 'fifo';
    }>(`${this.baseUrl}/policy/${encodeURIComponent(key)}`);
    return response.data;
  }

  async exportCacheData(format: 'json' | 'csv' = 'json'): Promise<Blob> {
    return await apiClient.download(`${this.baseUrl}/export?format=${format}`);
  }

  async importCacheData(file: File): Promise<{ imported: number; errors: string[] }> {
    const response = await apiClient.upload<{ imported: number; errors: string[] }>(`${this.baseUrl}/import`, file);
    return response.data;
  }

  async backupCache(): Promise<{ backupId: string; size: number }> {
    const response = await apiClient.post<{ backupId: string; size: number }>(`${this.baseUrl}/backup`);
    return response.data;
  }

  async restoreCache(backupId: string): Promise<{ restored: number; errors: string[] }> {
    const response = await apiClient.post<{ restored: number; errors: string[] }>(`${this.baseUrl}/restore`, { backupId });
    return response.data;
  }

  async getCacheBackups(): Promise<{
    id: string;
    createdAt: string;
    size: number;
    keyCount: number;
    description?: string;
  }[]> {
    const response = await apiClient.get<{
      id: string;
      createdAt: string;
      size: number;
      keyCount: number;
      description?: string;
    }[]>(`${this.baseUrl}/backups`);
    return response.data;
  }

  async deleteCacheBackup(backupId: string): Promise<void> {
    const response = await apiClient.delete(`${this.baseUrl}/backup/${backupId}`);
    if (!response.success) {
      throw new Error(response.message || 'Errore durante l\'eliminazione del backup cache');
    }
  }

  async optimizeCache(): Promise<{ optimized: number; freed: number }> {
    const response = await apiClient.post<{ optimized: number; freed: number }>(`${this.baseUrl}/optimize`);
    return response.data;
  }

  async getCacheConfiguration(): Promise<{
    maxMemory: number;
    defaultTtl: number;
    evictionPolicy: string;
    persistenceEnabled: boolean;
    compressionEnabled: boolean;
  }> {
    const response = await apiClient.get<{
      maxMemory: number;
      defaultTtl: number;
      evictionPolicy: string;
      persistenceEnabled: boolean;
      compressionEnabled: boolean;
    }>(`${this.baseUrl}/config`);
    return response.data;
  }

  async updateCacheConfiguration(config: {
    maxMemory?: number;
    defaultTtl?: number;
    evictionPolicy?: string;
    persistenceEnabled?: boolean;
    compressionEnabled?: boolean;
  }): Promise<void> {
    const response = await apiClient.put(`${this.baseUrl}/config`, config);
    if (!response.success) {
      throw new Error(response.message || 'Errore durante l\'aggiornamento della configurazione cache');
    }
  }
}

export const cacheService = new CacheService();
